import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { GET_AGENCY_PEER_POINT_PER_MONTH } from "../../api/query";
import { CREATE_OR_UPDATE_AGENCY_PEER_POINT_PER_MONTH } from "../../api/mutation";
import { showError, showSuccess } from "../common/ToastNotification";
import { useAuth } from "../../context/AuthContext";
import InputsNew from "../common/InputsNew";
import Loader from "../common/Loader";

interface PeerPointForm {
    peer_point_per_month: number;
}

// Validation schema
const peerPointSchema = yup.object().shape({
    peer_point_per_month: yup
        .number()
        .typeError('Please enter a valid number')
        .positive('Peer points must be positive')
        .integer('Peer points must be a whole number')
        .required('This field is required'),
});

const AddPeerPoint = () => {
    const { userInfo } = useAuth();
    const [isEditing, setIsEditing] = useState(false);

    const agencyId = userInfo?.agencies?.id;
    const employeeId = userInfo?.employee_id;

    // Query to get existing peer points
    const { data, loading, refetch } = useQuery(GET_AGENCY_PEER_POINT_PER_MONTH, {
        variables: { agency_id: parseInt(agencyId) },
        fetchPolicy: 'no-cache',
        skip: !agencyId,
    });

    // Mutation to create or update peer points
    const [createOrUpdatePeerPoint, { loading: mutationLoading }] = useMutation(
        CREATE_OR_UPDATE_AGENCY_PEER_POINT_PER_MONTH,
        {
            fetchPolicy: 'no-cache',
        }
    );

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue
    } = useForm<PeerPointForm>({
        resolver: yupResolver(peerPointSchema),
    });

    const agencyPeerPoints = data?.get_agency_peer_point_per_month?.agency_peer_points?.[0];

    const handleEdit = () => {
        if (agencyPeerPoints) {
            setValue('peer_point_per_month', agencyPeerPoints.peer_point_per_month);
        }
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        reset();
    };

    async function onSubmit(formData: PeerPointForm) {
        try {
            const response = await createOrUpdatePeerPoint({
                variables: {
                    input: {
                        peer_point_per_month: formData.peer_point_per_month,
                        agency_id: parseInt(agencyId),
                        created_by: parseInt(employeeId),
                        updated_by: parseInt(employeeId),
                    },
                },
            });

            if (response.data.create_or_update_agency_peer_point_per_month.status_code === 200 ||
                response.data.create_or_update_agency_peer_point_per_month.status_code === 201) {
                showSuccess(response.data.create_or_update_agency_peer_point_per_month.message);
                refetch();
                setIsEditing(false);
                reset();
            } else {
                showError(response.data.create_or_update_agency_peer_point_per_month.message);
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message);
            }
        }
    }

    return (
        <>
            <div className="w-full">
                {!isEditing && agencyPeerPoints ? (
                    <div className="flex items-center justify-between p-4 border rounded bg-gray-50 items-dark-blg">
                        <div>
                            <span className="text-sm text-gray-600 dark:text-gray-400">Peer Points Per Month:</span>
                            <span className="ml-2 text-lg font-bold dark:text-white">
                                {agencyPeerPoints.peer_point_per_month}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={handleEdit}
                            className="cursor-pointer px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                            Edit
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                            <InputsNew
                                contentPlaceholder="Peer Points Per Month"
                                type="number"
                                register={register('peer_point_per_month')}
                                errorMessage={errors.peer_point_per_month}
                            />
                            <div className="flex items-end gap-2">
                                <button
                                    type="submit"
                                    className="cursor-pointer px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                >
                                    {agencyPeerPoints ? 'Update' : 'Save'}
                                </button>
                                {isEditing && (
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="cursor-pointer px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>
                        </div>
                    </form>
                )}
            </div>
            {(loading || mutationLoading) && <Loader />}
        </>
    );
};

export default AddPeerPoint;
